import { z } from "zod";

const ACCEPTED_IMAGE_TYPES = ["jpeg", "jpg", "png", "webp"];
const ImgSchema = z.object({
  fileName: z.string(),
  name: z.string(),
  fileSize: z.number(),
  size: z.number(),
  key: z.string(),
  file: z.instanceof(File).optional(),
});
export const IMG_MAX_LIMIT = 1;

export const newsSchema = z
  .object({
    title: z
      .string({ required_error: "Le titre est obligatoire" })
      .min(3, { message: "Le titre est obligatoire" }),
    date: z.date({
      required_error: "La date est obligatoire",
    }),
    description: z
      .string({ required_error: "La description est obligatoire" })
      .min(1, { message: "La description est obligatoire" })
      .refine(
        (value) => {
          const plainText = value.replace(/<[^>]+>/g, "").trim();
          return plainText.length > 0;
        },
        { message: "Description est obligatoire" }
      ),
    image: z
      .array(ImgSchema)
      .max(IMG_MAX_LIMIT, {
        message: "Vous ne pouvez ajouter qu'une seule image.",
      })
      .refine(
        (images) => {
          if (images && images.length > 0) {
            const invalidImages = images.filter((img) => {
              const fileExtension = img.fileName.split(".").pop()?.toLowerCase();
              return fileExtension && !ACCEPTED_IMAGE_TYPES.includes(fileExtension);
            });
            return invalidImages.length === 0;
          }
          return true;
        },
        { message: "Le type de fichier de l'image n'est pas pris en charge." }
      )
      .optional(),
    isDraft: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    // L'image est requise seulement si ce n'est pas un brouillon
    if (!data.isDraft && (!data.image || data.image.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "L'image est obligatoire pour une actualité publiée.",
        path: ["image"],
      });
    }
  });

export type NewsFormValues = z.infer<typeof newsSchema>;

export const updatedNewsSchema = z
  .object({
    title: z
      .string()
      .min(3, { message: "Le titre doit comporter au moins 3 caractères." })
      .optional(),
    date: z
      .date({
        required_error: "La date est obligatoire",
      })
      .optional(),
    description: z
      .string({ required_error: "Description est obligatoire" })
      .optional(),
    image: z
      .array(ImgSchema)
      .max(IMG_MAX_LIMIT, {
        message: "Vous ne pouvez ajouter qu'une seule image.",
      })
      .refine(
        (images) => {
          if (images && images.length > 0) {
            const invalidImages = images.filter((img) => {
              const fileExtension = img.fileName
                .split(".")
                .pop()
                ?.toLowerCase();
              return (
                fileExtension && !ACCEPTED_IMAGE_TYPES.includes(fileExtension)
              );
            });
            return invalidImages.length === 0;
          }
          return true;
        },
        {
          message: "Le type de fichier de l'image n'est pas pris en charge.",
        }
      )
      .optional(),
    isDraft: z.boolean().optional(),
  })
  .partial();

export type UpdatedNewsSchema = z.infer<typeof updatedNewsSchema>;
